/* ========================================================================
   List Transitions
   ======================================================================== */

/*
 * List Transition
 * gathers all transition lists and adds a triggerTransition method on the element itself
 * this method can be called from outside the module to trigger the transition
 *
 * Usage:
 * add an event listener to a button, gather the transitionList(s) you want it to influence
 * call $transitionList.triggerTransition() on the transitionList(s)
 *
 * For simple lists, you can place a data-list-transition-toggle attribute on a button nested within the
 * $transitionList. This is already set up to transition the list element
 *
 */

export default class ListTransition {
  constructor() {
    this.init();
  }

  /*
   * compute indexes for items
   * if invert is true, invert indexing
   *
   * these computed indexes will be used to calculate the delays on the items
   * for example:
   * if you have 3 items, by default the computed indexes will be 1, 2 and 3
   * item 1 wil have a delay of 1 * --o-lt-per-item-delay (1 * 0.3 = 0.3s)
   * item 2 wil have a delay of 2 * --o-lt-per-item-delay (2 * 0.3 = 0.6s)
   * item 3 wil have a delay of 3 * --o-lt-per-item-delay (3 * 0.3 = 0.9s)
   *
   */
  computeItemIndexes($items, invert = false, topLevelOnly = false) {
    const takenIndexes = [];
    const availableIndexes = [];
    let highestIndex = 0;

    if (topLevelOnly) {
      $items = Array.from($items).filter(($singleItem) => {
        return !$singleItem.parentNode.closest("[data-list-transition-item]");
      });
    }

    // check which indexes are taken
    $items.forEach(($item) => {
      const index = $item.dataset.listTransitionItem;
      if (index) {
        takenIndexes.push(parseInt(index));
      }
    });

    // check which preset index is the highest
    takenIndexes.forEach((index) => {
      if (index > highestIndex) {
        highestIndex = index;
      }
    });

    // check how many items are in the array and check if that is higher than the highest preset index
    // set the highest of the two as the highest index
    const numItems = $items.length;
    highestIndex = Math.max(numItems, highestIndex);

    // fill an array with all available indexes
    for (let i = 1; i <= highestIndex; i++) {
      if (!takenIndexes.includes(i)) {
        availableIndexes.push(i);
      }
    }

    /* compute item indexes
     *
     * If list-transition-item has a numeric value, use that as the computed index.
     *
     * If list-transition-item has a value of "reset" set customComputedIndex to 0, and start counting from there.
     * For every next item that has a value of "reset", reset the customComputedIndex to 0 again.
     *
     * If list-transition-item has no value set, and no "reset" has occured, use the available indexes array.
     *
     * */
    let customComputedIndex = null;
    let highestComputedIndex = 0;
    $items.forEach(($item) => {
      const reset = $item.dataset.listTransitionItem === "reset";
      const customValue = parseInt($item.dataset.listTransitionItem);

      let index;
      if (reset) {
        customComputedIndex = 0;
      }

      if (parseInt(customValue)) {
        customComputedIndex = customValue;
      }

      if (customComputedIndex === null) {
        index =
          parseInt($item.dataset.listTransitionItem) ||
          availableIndexes.shift();
        $item.dataset.computedTransitionItemIndex = index;
      } else {
        $item.dataset.computedTransitionItemIndex = customComputedIndex;
        index = customComputedIndex;
        customComputedIndex++;
      }

      // store the highest computed index for optional inversion
      if (index > highestComputedIndex) {
        highestComputedIndex = index;
      }
    });

    // invert the order of the items if needed
    if (invert) {
      $items.forEach(($item) => {
        $item.dataset.computedTransitionItemIndex = (
          highestComputedIndex -
          parseInt($item.dataset.computedTransitionItemIndex)
        ).toString();
      });
    }

    /* make sure data-list-transition-items with the index set to "first" will always have an index of 0
     * and data-list-transition-items with the index set to "last" will always have an index of numItems */
    $items.forEach(($item) => {
      if ($item.dataset.listTransitionItem === "first") {
        $item.dataset.computedTransitionItemIndex = "0";
      } else if ($item.dataset.listTransitionItem === "last") {
        $item.dataset.computedTransitionItemIndex =
          highestComputedIndex.toString();
      }
    });
  }

  /*
   * set delay on items
   * the invert parameter is forwarded to computeItemIndexes
   * if noDelay is true, set the delay to zero seconds
   */
  setDelayOnItems(
    $transitionList,
    invert = false,
    noDelay = false,
    extraDelay = 0,
    topLevelOrderOnly = false,
  ) {
    const $items = $transitionList.querySelectorAll(
      "[data-list-transition-item]",
    );

    // set delay to 0 if noDelay is true
    if (noDelay) {
      $items.forEach(($item) => {
        $item.style.setProperty("--o-lt-delay", `0s`);
      });
      return;
    }

    // compute the item indexes
    this.computeItemIndexes($items, invert, topLevelOrderOnly);

    // set the delay
    const perItemDelayString = getComputedStyle(
      $transitionList,
    ).getPropertyValue("--o-lt-per-item-delay");
    let perItemDelay = parseFloat(perItemDelayString);
    if (perItemDelayString.includes("ms")) {
      perItemDelay = perItemDelay / 1000;
    }
    $items.forEach(($item) => {
      $item.style.setProperty(
        "--o-lt-delay",
        `${perItemDelay * $item.dataset.computedTransitionItemIndex + extraDelay
        }s`,
      );
    });
  }

  setDelayOnContainers($transitionList, noDelay = true) {
    const $containers = $transitionList.querySelectorAll(
      "[data-list-transition-container]",
    );
    let numItems = $transitionList.querySelectorAll(
      "[data-list-transition-item]",
    ).length;

    if (noDelay) {
      $containers.forEach(($container) => {
        $container.style.setProperty("--o-lt-delay", `0s`);
      });
    } else {
      const perItemDelay = parseFloat(
        getComputedStyle($transitionList).getPropertyValue(
          "--o-lt-per-item-delay",
        ),
      );
      $containers.forEach(($container) => {
        $container.style.setProperty(
          "--o-lt-delay",
          `${perItemDelay * numItems}s`,
        );
      });
    }
  }

  toggleContainers($transitionList, open = true, shallow = false) {
    let $containers = $transitionList.querySelectorAll(
      "[data-list-transition-container]",
    );
    if (shallow) {
      $containers = [...$containers].filter(($container) => {
        return $container.closest("[data-list-transition]") === $transitionList;
      });
    }
    $containers.forEach(($container) => {
      if (open) {
        $container.classList.remove("o-list-transition__container--collapsed");
      } else {
        $container.classList.add("o-list-transition__container--collapsed");
      }
    });
  }

  setMaxHeightOnContainers($transitionList) {
    // if no $transitionlist is provided, get all list-transition-containers on the page
    let $containers = ($transitionList || document)?.querySelectorAll(
      "[data-list-transition-container]",
    );

    const setMaxHeight = ($container) => {
      const $items = $container.querySelectorAll("[data-list-transition-item]");
      let height = 0;
      $items.forEach(($item) => {
        height += $item.clientHeight;
      });
      height += 100;
      $container.style.setProperty("--o-lt-container-height", `${height}px`);
    };

    $containers.forEach(setMaxHeight);
  }

  getLastTransitionItem($transitionList) {
    let highestIndex = 0;
    let $items = $transitionList.querySelectorAll(
      "[data-list-transition-item]",
    );
    let $lastItem = null;
    $items.forEach(($item) => {
      const currentIndex = parseInt($item.dataset.computedTransitionItemIndex);
      if (!highestIndex || currentIndex > highestIndex) {
        highestIndex = currentIndex;
        $lastItem = $item;
      }
    });

    return $lastItem;
  }

  markListAsOpen($transitionList) {
    $transitionList.classList.add("o-list-transition--in");
    $transitionList.classList.remove("o-list-transition--out");
    const $container = $transitionList.querySelector(
      "[data-list-transition-container]",
    );
    $container.classList.remove("o-list-transition__container--collapsed");
  }

  addAndRemoveClassFromTransitionList(
    $transitionList,
    addClasses,
    removeClasses,
    recursive = true,
  ) {
    if (typeof addClasses === "string") {
      addClasses = [addClasses];
    }

    if (typeof removeClasses === "string") {
      removeClasses = [removeClasses];
    }

    const $lists = [$transitionList];
    if (recursive) {
      $lists.push(
        ...$transitionList.querySelectorAll("[data-list-transition]"),
      );
    }

    $lists.forEach(($list) => {
      addClasses?.forEach((addClass) => {
        $list.classList.add(addClass);
      });
      removeClasses?.forEach((removeClass) => {
        $list.classList.remove(removeClass);
      });
    });
  }

  setTransitionIn($transitionList, shallow = false, topLevelOrderOnly = false) {
    const extraDelay = this._getDelay($transitionList)[0] ?? 0;

    const classList = $transitionList.classList;
    let isAbrupt = classList.contains("o-list-transition--no-transition");
    const containerClipsOverflow =
      $transitionList.querySelectorAll(
        ".o-list-transition__container--clip-overflow",
      ).length < 1;
    const isFromBelow = classList.contains("o-list-transition--in-from-below");

    /* set Delay on items and containers
     * if transition does not come in from below, invert the delay sequence on the items
     * if the transition is abrupt, set the delay to 0s
     * */
    this.setDelayOnItems(
      $transitionList,
      !isFromBelow,
      isAbrupt,
      extraDelay,
      topLevelOrderOnly,
    );
    this.setDelayOnContainers(
      $transitionList,
      isAbrupt || containerClipsOverflow,
    );

    /* set transition to in */
    this.addAndRemoveClassFromTransitionList(
      $transitionList,
      "o-list-transition--in",
      "o-list-transition--out",
      !shallow,
    );

    this.toggleContainers($transitionList, true, shallow);

    // dispatch event
    const event = new Event("list-open");
    $transitionList.dispatchEvent(event);
  }

  setTransitionOut(
    $transitionList,
    shallow = false,
    topLevelOrderOnly = false,
  ) {
    const extraDelay = this._getDelay($transitionList)[1] ?? 0;

    const classList = $transitionList.classList;
    const isAbrupt = classList.contains("o-list-transition--no-transition");
    const containerClips =
      $transitionList.querySelectorAll(
        ".o-list-transition__container--clip-overflow",
      ).length < 1;
    const outToBelow = classList.contains("o-list-transition--out-to-below");

    /* set Delay on items and containers
     * if transition goes out to below, invert the delay sequence on the items
     * if the transition is abrupt, set the delay to 0s
     * */
    this.setDelayOnItems(
      $transitionList,
      outToBelow,
      isAbrupt,
      extraDelay,
      topLevelOrderOnly,
    );
    this.setDelayOnContainers($transitionList, isAbrupt || containerClips);

    /*
     * get the last transition item and reset state of transitionList after last transition item has finished
     * this is not necessary for items with no transition */
    const $lastTransitionItem = this.getLastTransitionItem($transitionList);
    if (!isAbrupt) {
      const removeTransitionOut = (e) => {
        $transitionList.classList.remove("o-list-transition--out");
        e.target.removeEventListener("transitionend", removeTransitionOut);
        this.addAndRemoveClassFromTransitionList(
          $transitionList,
          undefined,
          "o-list-transition--out",
        );
      };
      $lastTransitionItem?.addEventListener(
        "transitionend",
        removeTransitionOut,
      );
    }

    // if there are no transition items, set the transition out immediately
    if (!isAbrupt && !$lastTransitionItem) {
      this.addAndRemoveClassFromTransitionList(
        $transitionList,
        undefined,
        "o-list-transition--out",
      );
    }

    /* set transition to out */
    this.addAndRemoveClassFromTransitionList(
      $transitionList,
      "o-list-transition--out",
      "o-list-transition--in",
    );

    this.toggleContainers($transitionList, false, shallow);

    // dispatch event
    const event = new Event("list-closed");
    $transitionList.dispatchEvent(event);
  }

  triggerTransition($transitionList, manualState = undefined) {
    manualState = this._interpretManualState(manualState);

    // if there are no transition-items, do nothing
    if (
      $transitionList.querySelectorAll("[data-list-transition-item]").length < 1
    ) {
      return;
    }

    // if explicit set to close, close the transition
    if (manualState === "close") {
      this.setTransitionOut($transitionList);
      return "closed";
    }

    // if explicit set to open, open the transition
    if (manualState === "open") {
      this.setTransitionIn($transitionList, true);
      return "open";
    }

    // if the transition has the out modifier
    if (
      $transitionList.classList.contains("o-list-transition--out") ||
      !$transitionList.classList.contains("o-list-transition--in")
    ) {
      this.setTransitionIn($transitionList);
      return "open";
    } else {
      this.setTransitionOut($transitionList);
      return "closed";
    }
  }

  // utility function for triggerTransition method
  _interpretManualState(manualState) {
    // if manualState is set, interpret it
    manualState = manualState?.replace(/closed/i, "close");
    manualState = manualState?.replace(/opened/i, "open");
    if (manualState && manualState !== "open" && manualState !== "close") {
      console.warn(
        "manualState is not valid",
        'manualState can only be "open" or "close"',
      );
      manualState = undefined;
    }
    return manualState;
  }

  closeSublists($transitionList) {
    $transitionList
      .querySelectorAll("[data-list-transition]")
      .forEach(($sublist) => {
        $sublist.triggerTransition("close");
      });
  }

  // utlitity for trigger transition
  _getDelay($transitionList) {
    let extraDelayIn = 0;
    let extraDelayOut = 0;
    let delayString = $transitionList.dataset.listTransition; // || $transitionList.dataset.listTransitionSublist;
    delayString =
      delayString === undefined
        ? $transitionList.dataset.listTransitionSublist
        : delayString;
    const regex = /(delay-(?:in|out)=).*(?=s)/;

    if (delayString.includes("delay-in=")) {
      extraDelayIn = parseFloat(delayString.match(regex)[0].split("=")[1]);
    }
    if (delayString.includes("delay-out=")) {
      extraDelayOut = parseFloat(delayString.match(regex)[0].split("=")[1]);
    }
    return [extraDelayIn, extraDelayOut];
  }
  _delayIsInMiliseconds(delayString) {
    return !!delayString.includes("ms");
  }

  isVisible($transition) {
    return $transition.classList.contains("o-list-transition--in");
  }

  isClosedOrClosing($transition) {
    return !$transition.classList.contains("o-list-transition--in");
  }

  setupElementMethods() {
    const $allLists = document.querySelectorAll("[data-list-transition]");
    $allLists.forEach(($transitionList) => {
      $transitionList.triggerTransition = (manualState = undefined) => {
        return this.triggerTransition($transitionList, manualState);
      };

      $transitionList.setTransitionIn = (
        shallow = false,
        topLevelOrderOnly = false,
      ) => {
        this.setTransitionIn($transitionList, shallow, topLevelOrderOnly);
      };

      $transitionList.setTransitionOut = (
        shallow = false,
        topLevelOrderOnly = false,
      ) => {
        this.setTransitionOut($transitionList, shallow, topLevelOrderOnly);
      };

      $transitionList.closeSublists = () => {
        this.closeSublists($transitionList);
      };

      $transitionList.isVisible = () => {
        return this.isVisible($transitionList);
      };

      $transitionList.isClosedOrClosing = () => {
        return this.isClosedOrClosing($transitionList);
      };

      $transitionList.markListAsOpen = () => {
        this.markListAsOpen($transitionList);
      };

      $transitionList.setMaxHeightOnContainers = () =>
        this.setMaxHeightOnContainers($transitionList);
    });
  }

  onToggle($toggle) {
    const $transitionList = $toggle.closest("[data-list-transition]");
    console.assert($transitionList !== null, "no transition list found");
    $transitionList?.triggerTransition();
  }

  setupToggle($toggle) {
    $toggle.addEventListener("click", (_) => {
      this.onToggle($toggle);
    });
  }

  init() {
    this.setupElementMethods();
    this.setMaxHeightOnContainers();
    window.addEventListener("resize", () => this.setMaxHeightOnContainers());
    this.$toggles = document.querySelectorAll("[data-list-transition-toggle]");
    this.$toggles.forEach(($toggle) => {
      this.setupToggle($toggle);
    });
  }

  static setupLists() {
    return new ListTransition();
  }
}

ListTransition.setupLists();
